// filters.js

import { process, round, processAggregated } from "./reports";
import { applyTargetsToEntries } from "./targets";

import {
    getWeekNumberSince2000,
    MAX_DATE,
    MIN_DATE,
    adjustLeapYear,
} from "./dates";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
    _DATATYPE_CHECKIN,
    _DATATYPE_CHECKIN_NAME,
    _DATATYPE_CHECKOUT,
    _DATATYPE_CHECKOUT_NAME,
    _DATATYPE_RESDATE,
    _DATATYPE_RESDATE_NAME,
    _DATATYPE_SEARCHES,
} from "./dataTypes";

dayjs.extend(utc);

export const DATATYPE_RESDATE = _DATATYPE_RESDATE;
export const DATATYPE_RESDATE_NAME = _DATATYPE_RESDATE_NAME;
export const DATATYPE_CHECKIN = _DATATYPE_CHECKIN;
export const DATATYPE_CHECKIN_NAME = _DATATYPE_CHECKIN_NAME;
export const DATATYPE_CHECKOUT = _DATATYPE_CHECKOUT;
export const DATATYPE_CHECKOUT_NAME = _DATATYPE_CHECKOUT_NAME;
export const DATATYPE_SEARCHES = _DATATYPE_SEARCHES;

export const PRESET_YESTERDAY = "Yesterday";
export const PRESET_TODAY = "Today";
export const PRESET_TOMORROW = "Tomorrow";
export const PRESET_MONTH_TO_DATE = "Month to date";
export const PRESET_YEAR_TO_DATE = "Year to date";
export const PRESET_THIS_WEEK = "This week";
export const PRESET_THIS_MONTH = "This month";
export const PRESET_THIS_QUARTER = "This quarter";
export const PRESET_THIS_YEAR = "This year";
export const PRESET_LAST_YEAR = "Last year";
export const PRESET_LAST_7_DAYS = "Last 7 days";
export const PRESET_LAST_30_DAYS = "Last 30 days";
export const PRESET_LAST_FULL_MONTH = "Last month";
export const PRESET_LAST_3_MONTHS = "Last 3 months";
export const PRESET_LAST_6_MONTHS = "Last 6 months";
export const PRESET_LAST_12_MONTHS = "Last 12 months";
export const PRESET_NEXT_MONTH = "Next month";
export const PRESET_NEXT_7_DAYS = "Next 7 days";
export const PRESET_NEXT_30_DAYS = "Next 30 days";
export const PRESET_NEXT_3_MONTHS = "Next 3 months";
export const PRESET_NEXT_6_MONTHS = "Next 6 months";
export const PRESET_NEXT_12_MONTHS = "Next 12 months";
export const PRESET_CUSTOM = "Custom";
export const PRESET_CHOOSE_MONTH = "Month";
export const PRESET_ANYTIME = "Anytime";

export const PRESET_BOOKING_GROUP = [
    PRESET_CUSTOM,
    PRESET_MONTH_TO_DATE,
    PRESET_YEAR_TO_DATE,
    PRESET_THIS_WEEK,
    PRESET_THIS_QUARTER,
    PRESET_THIS_YEAR,
    PRESET_LAST_FULL_MONTH,
    PRESET_LAST_YEAR,
    PRESET_CHOOSE_MONTH,
    PRESET_TOMORROW,
    PRESET_YESTERDAY,
    PRESET_LAST_7_DAYS,
    PRESET_LAST_30_DAYS,
    PRESET_LAST_3_MONTHS,
    PRESET_LAST_6_MONTHS,
    PRESET_LAST_12_MONTHS,
    PRESET_NEXT_MONTH,
    PRESET_NEXT_7_DAYS,
    PRESET_NEXT_30_DAYS,
    PRESET_NEXT_3_MONTHS,
    PRESET_NEXT_6_MONTHS,
    PRESET_NEXT_12_MONTHS,
];

export const PRESET_RESDATE_GROUP = [
    PRESET_CUSTOM,
    PRESET_ANYTIME,
    PRESET_MONTH_TO_DATE,
    PRESET_YEAR_TO_DATE,
    PRESET_THIS_WEEK,
    PRESET_THIS_QUARTER,
    PRESET_LAST_FULL_MONTH,
    PRESET_CHOOSE_MONTH,
    PRESET_YESTERDAY,
    PRESET_LAST_7_DAYS,
    PRESET_LAST_30_DAYS,
    PRESET_LAST_3_MONTHS,
    PRESET_LAST_6_MONTHS,
    PRESET_LAST_12_MONTHS,
];
export const PRESET_RESDATE_CHOOSE_MONTH = "Choose month";
export const PRESET_RESDATE_DEFAULT = PRESET_ANYTIME;

export const PRESET_SELECTED_PERIOD = "Selected period";
export const PRESET_CURRENT_MONTH = "Current month";

export const PRESET_ARRIVAL_DEFAULT = PRESET_ANYTIME;
export const PRESET_ARRIVAL_GROUP = [
    PRESET_ANYTIME,
    PRESET_SELECTED_PERIOD,
    PRESET_THIS_WEEK,
    PRESET_THIS_QUARTER,
    PRESET_NEXT_3_MONTHS,
    PRESET_NEXT_12_MONTHS,
    PRESET_CURRENT_MONTH,
    PRESET_NEXT_MONTH,
    PRESET_CHOOSE_MONTH,
];

export const PRESET_WEEK_RANGE_PREFIX = "WEEK_RANGE:";

export const isWeekRangePreset = (v) =>
    typeof v === "string" && v.startsWith(PRESET_WEEK_RANGE_PREFIX);

export const parseWeekRangePreset = (v) => {
    if (!isWeekRangePreset(v)) return null;
    const raw = v.slice(PRESET_WEEK_RANGE_PREFIX.length);
    const [fromDay, toDay] = raw.split("|");
    return { fromDay, toDay };
};

export const buildWeekRangePresets = (from, to) => {
    const fromD = dayjs.isDayjs(from) ? from : dayjs.utc(from);
    const toD = dayjs.isDayjs(to) ? to : dayjs.utc(to);

    // Expand to full weeks so the range covers complete week blocks
    const start = fromD.startOf("day").startOf("week");
    const end = toD.startOf("day").endOf("week");

    const out = [];
    let cursor = start;

    while (cursor.isBefore(end, "day") || cursor.isSame(end, "day")) {
        const fromW = cursor.startOf("day");
        const toW = cursor.add(6, "day").startOf("day");

        const fromDay = formatDate(fromW);
        const toDay = formatDate(toW);

        out.push({
            value: `${PRESET_WEEK_RANGE_PREFIX}${fromDay}|${toDay}`,
            label: `${fromW.format("D MMM")} – ${toW.format("D MMM YYYY")}`,
        });

        cursor = cursor.add(7, "day");
    }

    return out; // all weeks in the range
};

/**
 * Returns the date range [fromDateGroup, toDateGroup] for the given date-based group combination.
 * If no date group is specified, it returns the default range defined by the user (userFrom, userTo).
 *
 * @param {Object} item         Object containing booking data. Requires:
 *                              item[GROUP_DATE_YEAR], item[GROUP_DATE_MONTH],
 *                              item[GROUP_DATE_DAY], item[GROUP_DATE_WEEK]...
 * @param {Array<string>} groups Array of groups being used (groupBy).
 * @param {String} userFrom     Date "YYYY-MM-DD" defined by the user (fallback).
 * @param {String} userTo       Date "YYYY-MM-DD" defined by the user (fallback).
 * @returns {Object}            { fromDateGroup: string, toDateGroup: string }
 */
function getDateRangeForGroup(item, groups, userFrom, userTo) {
    // Check if there is any time-based grouping
    const hasYear = groups.includes(GROUP_DATE_YEAR);
    const hasMonth = groups.includes(GROUP_DATE_MONTH);
    const hasDay = groups.includes(GROUP_DATE_DAY);
    const hasWeek = groups.includes(GROUP_DATE_WEEK);

    // If there is no date grouping, return the user's range directly
    if (!hasYear && !hasMonth && !hasDay && !hasWeek) {
        return {
            fromDateGroup: userFrom,
            toDateGroup: userTo,
        };
    }

    // Helper function for safe integer parsing
    const parseIntSafe = (val, defaultVal) => {
        const n = parseInt(val, 10);
        return isNaN(n) ? defaultVal : n;
    };

    // Use the "selectedDate" as a fallback for year, month, etc.
    const fallbackDate = dayjs(item.selectedDate);

    // Extract year, month, day, and week from the item or from the fallback date
    const year = hasYear
        ? parseIntSafe(item[GROUP_DATE_YEAR], fallbackDate.year())
        : fallbackDate.year();

    const month = hasMonth
        ? parseIntSafe(item[GROUP_DATE_MONTH], fallbackDate.month() + 1) - 1
        : fallbackDate.month();

    const day = hasDay
        ? parseIntSafe(item[GROUP_DATE_DAY], fallbackDate.date())
        : fallbackDate.date();

    const weekNumber = hasWeek ? parseIntSafe(item[GROUP_DATE_WEEK], 1) : 1; // fallback just in case

    let fromDate, toDate;

    // Prioritize the most granular grouping:
    // DAY > WEEK > MONTH > YEAR
    if (hasDay) {
        // Exact YYYY, MM, DD
        fromDate = new Date(Date.UTC(year, month, day)); // parsedDateToDayjs(year, month, day);
        toDate = new Date(fromDate.getTime()); // same day // dayjs(fromDate);
    } else if (hasWeek) {
        // With YEAR + WEEK we define the corresponding "complete" week.
        // This depends on your definition of "week". Here, we assume that
        // "WeekNumber" is the N-th week since the first week of the year 2000 or similar.
        // If your "WeekNumber" represents "week of the year", you should adjust the logic.

        // EXAMPLE with "week of the year" (1..52):
        //   1) Take the first day of the year (e.g. January 1st).
        //   2) Add (weekNumber - 1) * 7 days.
        //   3) The "week" runs from that day to +6 days.

        // NOTE: if your weekNumber is calculated with getWeekNumberSince2000,
        // it might not be "week of the year" but an absolute week number since 2000.
        // Adjust the logic as needed.

        // For a simple example:
        //   "Week 10 of 2023" => from = "2023-01-01" + 9*7 days => to = +6 days

        const startOfYear = new Date(Date.UTC(year, 0, 1)); // parsedDateToDayjs(year, 0, 1);
        const startOfYearWeekN = getWeekNumberSince2000(startOfYear);
        const fromDateTmp = new Date(startOfYear); // dayjs(startOfYear); // ;
        console.log("WEEK PROCESSING ", { startOfYear, fromDateTmp });
        fromDateTmp.setUTCDate(
            fromDateTmp.getUTCDate() + (weekNumber - startOfYearWeekN) * 7,
        );
        //fromDateTmp.add(weekNumber - 1, "week");
        fromDate = fromDateTmp;

        toDate = new Date(fromDate.getTime());

        toDate.setUTCDate(toDate.getUTCDate() + 6);
    } else if (hasMonth) {
        // YEAR + MONTH => complete month
        const y = hasYear ? year : fallbackDate.year();
        const m = month; // already 0-based
        fromDate = new Date(Date.UTC(y, m, 1)); // parsedDateToDayjs(y, m, 1);
        // End of month: using day 0 of the next month (trick) yields the last day of the current month (31, 30, 28, etc.)
        toDate = new Date(Date.UTC(y, m + 1, 0)); // parsedDateToDayjs(y, m, 1);
    } else if (hasYear) {
        // Only YEAR => entire year
        fromDate = new Date(Date.UTC(year, 0, 1)); //parsedDateToDayjs(year, 0, 1)
        toDate = new Date(Date.UTC(year, 11, 31)); //parsedDateToDayjs(year, 11, 31)
    }

    // Convert to string in YYYY-MM-DD format
    return {
        fromDateGroup: formatDate(fromDate),
        toDateGroup: formatDate(toDate),
    };
}

const normalizeOfferTitle = (v) => String(v ?? "").trim();

const normalizeOfferId = (v) => {
    const value = String(v ?? "").trim();

    if (
        value === "" ||
        value.toLowerCase() === "null" ||
        value.toLowerCase() === "undefined"
    ) {
        return "";
    }

    return value;
};

const toDateMs = (v) => {
    if (v === null || v === undefined || v === "") return -1;

    if (typeof v === "number" && Number.isFinite(v)) {
        return v;
    }

    const raw = String(v).trim();
    if (!raw) return -1;

    // Handle unix timestamp in milliseconds as string
    if (/^\d+$/.test(raw)) {
        const numeric = Number(raw);
        return Number.isFinite(numeric) ? numeric : -1;
    }

    const parsed = Date.parse(raw);
    return Number.isFinite(parsed) ? parsed : -1;
};

const toDateOnlyString = (v) => {
    const ms = toDateMs(v);
    if (!Number.isFinite(ms) || ms < 0) return "";

    return new Date(ms).toISOString().slice(0, 10);
};

// Build linking directly from the loaded reservations.
// Identity = OfferID
// Visible label = latest OfferTitle by ResDate inside the loaded dataset
const buildOfferLinkingFromBookings = (bookings) => {
    const rows = Array.isArray(bookings) ? bookings : [];

    const byOfferId = {};
    const offerToMain = {};
    const renameInfoByMain = {};

    rows.forEach((booking) => {
        const offerTitle = normalizeOfferTitle(booking?.OfferTitle);
        const offerId = normalizeOfferId(booking?.OfferID);
        const resDate = toDateOnlyString(booking?.ResDate);
        const resDateMs = toDateMs(booking?.ResDate);

        if (!offerTitle) return;

        // Rows without OfferID stay standalone.
        // We do not merge them blindly.
        if (!offerId) {
            const previous = renameInfoByMain[offerTitle];

            if (!offerToMain[offerTitle]) {
                offerToMain[offerTitle] = offerTitle;
            }

            if (!previous) {
                renameInfoByMain[offerTitle] = {
                    main: offerTitle,
                    offerId: null,
                    latestResDate: resDate,
                    latestResDateMs: resDateMs,
                    names: [
                        {
                            name: offerTitle,
                            lastResDate: resDate,
                            lastResDateMs: resDateMs,
                        },
                    ],
                };
            } else if (resDateMs > (previous.latestResDateMs ?? -1)) {
                renameInfoByMain[offerTitle] = {
                    ...previous,
                    latestResDate: resDate,
                    latestResDateMs: resDateMs,
                    names: [
                        {
                            name: offerTitle,
                            lastResDate: resDate,
                            lastResDateMs: resDateMs,
                        },
                    ],
                };
            }

            return;
        }

        if (!byOfferId[offerId]) {
            byOfferId[offerId] = {
                latestResDateMs: -1,
                latestResDate: "",
                latestTitle: "",
                titles: {},
            };
        }

        const bucket = byOfferId[offerId];

        const previousTitleInfo = bucket.titles[offerTitle] ?? {
            count: 0,
            latestResDateMs: -1,
            latestResDate: "",
        };

        const nextTitleInfo =
            resDateMs > previousTitleInfo.latestResDateMs
                ? {
                      count: previousTitleInfo.count + 1,
                      latestResDateMs: resDateMs,
                      latestResDate: resDate,
                  }
                : {
                      count: previousTitleInfo.count + 1,
                      latestResDateMs: previousTitleInfo.latestResDateMs,
                      latestResDate: previousTitleInfo.latestResDate,
                  };

        bucket.titles[offerTitle] = nextTitleInfo;

        const shouldReplaceMain =
            resDateMs > bucket.latestResDateMs ||
            (resDateMs === bucket.latestResDateMs &&
                offerTitle.localeCompare(bucket.latestTitle) < 0);

        if (shouldReplaceMain) {
            bucket.latestResDateMs = resDateMs;
            bucket.latestResDate = resDate;
            bucket.latestTitle = offerTitle;
        }
    });

    Object.entries(byOfferId).forEach(([offerId, bucket]) => {
        const main = normalizeOfferTitle(bucket.latestTitle);
        if (!main) return;

        const names = Object.entries(bucket.titles)
            .map(([name, info]) => ({
                name,
                lastResDate: info?.latestResDate ?? "",
                lastResDateMs: info?.latestResDateMs ?? -1,
            }))
            .sort((a, b) => {
                if (a.name === main) return -1;
                if (b.name === main) return 1;

                const latestDiff = b.lastResDateMs - a.lastResDateMs;
                if (latestDiff !== 0) return latestDiff;

                return a.name.localeCompare(b.name);
            });

        names.forEach((item) => {
            offerToMain[item.name] = main;
        });

        renameInfoByMain[main] = {
            main,
            offerId,
            latestResDate: bucket.latestResDate,
            latestResDateMs: bucket.latestResDateMs,
            names,
        };
    });
    return { offerToMain, renameInfoByMain };
};

// Normalize the Offers filter values to the canonical visible title
const mapOfferFiltersToCanonical = (offersFilterArray, offerToMain) => {
    const arr = Array.isArray(offersFilterArray) ? offersFilterArray : [];

    if (!offerToMain || Object.keys(offerToMain).length === 0) {
        return arr;
    }

    return arr.map((v) => {
        const str = String(v ?? "");
        const isNeg = str.startsWith("!");
        const raw = isNeg ? str.slice(1) : str;

        const key = normalizeOfferTitle(raw);
        const mapped = offerToMain[key] ?? raw;

        return isNeg ? `!${mapped}` : mapped;
    });
};

// Rewrite each booking so grouping/filtering keep using OfferTitle,
// but the canonical title is decided by OfferID.
const applyOfferLinkingToBookings = (data, offerToMain) => {
    if (!offerToMain || Object.keys(offerToMain).length === 0) {
        return data;
    }

    return (Array.isArray(data) ? data : []).map((e) => {
        const originalTitle = normalizeOfferTitle(e?.OfferTitle);
        if (!originalTitle) return e;

        const mainTitle = offerToMain[originalTitle] ?? originalTitle;

        if (String(e?.OfferTitle) === mainTitle) {
            return e;
        }

        return {
            ...e,
            OfferTitleOriginal: e?.OfferTitle ?? "",
            OfferTitle: mainTitle,
        };
    });
};

const getSessionsMetrics = (sessions, fromDay, toDay) => {
    const from = new Date(fromDay);
    const to = new Date(toDay);
    to.setDate(to.getDate() + 1);

    const rows = (sessions ?? []).filter((row) => {
        const date = new Date(row.ReportDate);
        return from <= date && date < to;
    });

    return rows.reduce(
        (acc, row) => ({
            sessionsTotal: acc.sessionsTotal + (row.AllSessions ?? 0),
            bookingEngineSessions:
                acc.bookingEngineSessions + (row.BookingEngineSessions ?? 0),
        }),
        { sessionsTotal: 0, bookingEngineSessions: 0 },
    );
};

const applySessionsMetrics = (result, sessions, fromDay, toDay) => {
    const { sessionsTotal, bookingEngineSessions } = getSessionsMetrics(
        sessions,
        fromDay,
        toDay,
    );

    result.sessionsTotal = sessionsTotal;
    result.bookingEngineSessions = bookingEngineSessions;
    result.bookingEngineSessionsRate = sessionsTotal
        ? round((bookingEngineSessions / sessionsTotal) * 100, 2)
        : 0;
    result.perSessionValue = sessionsTotal
        ? round(result.revenueTotal / sessionsTotal, 2)
        : 0;
    result.perBESessionValue = bookingEngineSessions
        ? round(result.revenueTotal / bookingEngineSessions, 2)
        : 0;
    result.websiteConversionRate = sessionsTotal
        ? round((result.bookingsTotal / sessionsTotal) * 100, 2)
        : 0;
    result.BEConversionRate = bookingEngineSessions
        ? round((result.bookingsTotal / bookingEngineSessions) * 100, 2)
        : 0;
};

const normalizeMergeValue = (value) => {
    const normalized = String(value ?? "").trim();

    return normalized &&
        !["null", "undefined", "nan"].includes(normalized.toLowerCase())
        ? normalized
        : "";
};

const getBookingMergeId = (row) => {
    const reservationID = normalizeMergeValue(row?.ReservationID);
    if (reservationID) return `reservation:${reservationID}`;

    const aggregationID = normalizeMergeValue(row?.AggregationID);
    if (aggregationID) return `aggregation:${aggregationID}`;

    return [
        "fallback",
        normalizeMergeValue(row?.selectedDate),
        normalizeMergeValue(row?.Country),
        normalizeMergeValue(row?.CancelStatus),
        normalizeMergeValue(row?.Type),
        normalizeMergeValue(row?.ResTypeID),
        normalizeMergeValue(row?.RoomName),
        normalizeMergeValue(row?.OfferID),
        normalizeMergeValue(row?.OfferTitle),
        normalizeMergeValue(row?.VoucherName),
    ].join("|");
};

export const mergeBookingsWithoutDuplicates = (...lists) => {
    const used = new Set();

    return lists
        .flatMap((list) => (Array.isArray(list) ? list : []))
        .filter((row) => {
            const key = getBookingMergeId(row);

            if (used.has(key)) return false;

            used.add(key);
            return true;
        });
};

export const getComparisonDates = (js) => {
    const comparisonBase = js.filters?.Comparison || DEFAULT_COMPARISON;

    let fromDayPrevious = dayjs(js.fromDay);
    let toDayPrevious = dayjs(js.toDay);

    let fromDayPreviousBookings = MIN_DATE;
    let toDayPreviousBookings = MAX_DATE;

    let fromDayPreviousArrivals = MIN_DATE;
    let toDayPreviousArrivals = MAX_DATE;

    /**
     * Adjusting Leap Day
     *
     * We can't change all dates on the 29th of February because
     * sometimes a 29th of February is expected.
     *
     * It only matters for year comparisons, where a non-existent
     * 29th of February in the previous year would become 1st of March.
     *
     * With custom, previous month or previous week comparisons,
     * we assume the selected dates are intentional.
     */

    const comparisonShift = {
        [COMPARISON_PREVIOUS_YEAR]: [1, "year", true],
        [COMPARISON_PREVIOUS_MONTH]: [1, "month", false],
        [COMPARISON_PREVIOUS_WEEK]: [7, "day", false],
        [COMPARISON_SAME_TIME_LAST_YEAR]: [1, "year", true],
    }[comparisonBase];

    // Keeps open ranges open.
    // If the user selected "Anytime", MIN_DATE and MAX_DATE aren't shifted.
    const shiftLimit = (value, fallback, amount, unit, useLeapYear) => {
        const date = dayjs(value);

        if (!date.isValid()) return fallback;

        const formatted = formatDate(date);

        if (
            formatted === formatDate(MIN_DATE) ||
            formatted === formatDate(MAX_DATE)
        ) {
            return fallback;
        }

        return (useLeapYear ? adjustLeapYear(date) : date).subtract(
            amount,
            unit,
        );
    };

    if (comparisonBase === COMPARISON_CUSTOM) {
        // Custom comparison only changes the main date range.
        // Arrival and booking limits stay open unless custom values
        // are explicitly defined for them in the future.
        fromDayPrevious = dayjs(
            js.filters?.ComparisonCustom?.fromDay ?? js.fromDay,
        );
        toDayPrevious = dayjs(js.filters?.ComparisonCustom?.toDay ?? js.toDay);
    } else if (comparisonShift) {
        const [amount, unit, useLeapYear] = comparisonShift;

        // Main period
        fromDayPrevious = (
            useLeapYear ? adjustLeapYear(fromDayPrevious) : fromDayPrevious
        ).subtract(amount, unit);

        toDayPrevious = (
            useLeapYear ? adjustLeapYear(toDayPrevious) : toDayPrevious
        ).subtract(amount, unit);

        // Arrival period
        fromDayPreviousArrivals = shiftLimit(
            js.fromArrivalDay,
            MIN_DATE,
            amount,
            unit,
            useLeapYear,
        );
        toDayPreviousArrivals = shiftLimit(
            js.toArrivalDay,
            MAX_DATE,
            amount,
            unit,
            useLeapYear,
        );

        // ResDate - Bookings
        fromDayPreviousBookings = shiftLimit(
            js.fromResDateDay,
            MIN_DATE,
            amount,
            unit,
            useLeapYear,
        );
        toDayPreviousBookings = shiftLimit(
            js.toResDateDay,
            MAX_DATE,
            amount,
            unit,
            useLeapYear,
        );
    }

    if (comparisonBase === COMPARISON_SAME_TIME_LAST_YEAR) {
        // "As-of" date for SAME_TIME_LAST_YEAR.
        const todayLastYear = adjustLeapYear(
            dayjs.utc().startOf("day"),
        ).subtract(1, "year");

        // Keeps an earlier selected end date, but prevents ResDate
        // comparisons from going beyond today's equivalent last year.
        const capEndDate = (date) =>
            dayjs(date).isAfter(todayLastYear, "day") ? todayLastYear : date;

        // ResDate uses the elapsed part of the selected period.
        // CheckIn and CheckOut keep the complete comparison period.
        if (js.dataType === DATATYPE_RESDATE) {
            toDayPrevious = capEndDate(toDayPrevious);
        }

        // The additional booking-date filter follows the same pacing rule.
        toDayPreviousBookings = capEndDate(toDayPreviousBookings);
    }

    return {
        comparisonBase,
        fromDayPrevious: formatDate(fromDayPrevious),
        toDayPrevious: formatDate(toDayPrevious),
        fromDayPreviousArrivals: formatDate(fromDayPreviousArrivals),
        toDayPreviousArrivals: formatDate(toDayPreviousArrivals),
        fromDayPreviousBookings: formatDate(fromDayPreviousBookings),
        toDayPreviousBookings: formatDate(toDayPreviousBookings),
    };
};

//   ____
//  |  _ \ _ __ ___   ___ ___  ___ ___
//  | |_) | '__/ _ \ / __/ _ \/ __/ __|
//  |  __/| | | (_) | (_|  __/\__ \__ \
//  |_|   |_|  \___/ \___\___||___/___/
export const process_ = ({
    js,
    dataBookings,
    hotelID,
    dataSimple,
    dataTargets,
    processFn = process,
}) => {
    const rawDataToUse = dataBookings.data ?? [];
    const hasComparisonPeriod = rawDataToUse.some(
        (row) => row?.__comparisonPeriod,
    );

    let dataToUse = hasComparisonPeriod
        ? mergeBookingsWithoutDuplicates(
              rawDataToUse.filter(
                  (row) => row.__comparisonPeriod === "current",
              ),
          )
        : mergeBookingsWithoutDuplicates(rawDataToUse);

    let dataToUseComparison = hasComparisonPeriod
        ? mergeBookingsWithoutDuplicates(
              rawDataToUse.filter(
                  (row) => row.__comparisonPeriod === "comparison",
              ),
          )
        : null;

    const sessions = dataSimple?.data?.Sessions2 ?? [];

    const {
        fromDay,
        toDay,
        fromArrivalDay,
        toArrivalDay,
        fromResDateDay,
        toResDateDay,
    } = js;

    const {
        Countries = [],
        Rooms = [],
        Offers = [],
        Types = [],
        Extras = [],
        VoucherNames = [],
        Cancellations = "included",
    } = js.filters ?? {};

    const { offerToMain, renameInfoByMain } = buildOfferLinkingFromBookings(
        hasComparisonPeriod
            ? [...dataToUse, ...dataToUseComparison]
            : dataToUse,
    );

    const OffersEffective = mapOfferFiltersToCanonical(Offers, offerToMain);

    dataToUse = applyOfferLinkingToBookings(dataToUse, offerToMain);
    dataToUseComparison = hasComparisonPeriod
        ? applyOfferLinkingToBookings(dataToUseComparison, offerToMain)
        : dataToUse;

    const {
        comparisonBase,
        fromDayPrevious,
        toDayPrevious,
        fromDayPreviousArrivals,
        toDayPreviousArrivals,
        fromDayPreviousBookings,
        toDayPreviousBookings,
    } = getComparisonDates(js);

    // decides if the element must be included according to the Cancellation filter
    const FilterCancellations = (element) =>
        CANCELLATIONS_FILTER[Cancellations].includeCancellation(element);

    const FilterData = (
        data,
        from_,
        to_,
        fromArrival_ = MIN_DATE,
        toArrival_ = MAX_DATE,
        fromBooking_ = MIN_DATE,
        toBooking_ = MAX_DATE,
        extras = {},
    ) => {
        const fromDate = new Date(formatDate(from_));
        const toPlusOneDay = new Date(formatDate(to_));
        toPlusOneDay.setUTCDate(toPlusOneDay.getUTCDate() + 1);

        const fromArrivalDate = new Date(formatDate(fromArrival_));
        const toArrivalPlusOneDay = new Date(formatDate(toArrival_));
        toArrivalPlusOneDay.setUTCDate(toArrivalPlusOneDay.getUTCDate() + 1);

        const fromBookingDate = new Date(formatDate(fromBooking_));
        const toBookingPlusOneDay = new Date(formatDate(toBooking_));
        toBookingPlusOneDay.setUTCDate(toBookingPlusOneDay.getUTCDate() + 1);

        let aux = data.filter((e, index) => {
            let date = new Date(
                helperType(js.dataType, e.ResDate, e.CheckOut, e.CheckIn),
            );

            let isVoucher = e.Type === "Voucher";

            let arrivalDate = new Date(e.CheckIn);
            let bookingDate = new Date(e.ResDate);

            let filteredCancellation = FilterCancellations(e);

            let isCountry = true;
            let isOffer = true;
            let isRoom = true;
            let isType = true;
            let isExtras = true;
            let isVoucherName = true;

            const checkFilter = (array, item) => {
                if (array.length > 0) {
                    const exclude = array.some((i) => i.startsWith("!"));
                    if (exclude) {
                        return !array.some((i) => item === i.slice(1));
                    } else {
                        return array.includes(item);
                    }
                }
                return true;
            };

            if (Countries.length > 0) {
                isCountry = checkFilter(Countries, e.Country);
            }
            if (OffersEffective.length > 0) {
                // uses normalized filters with canonical OfferTitle names
                isOffer = checkFilter(OffersEffective, e.OfferTitle);
            }
            if (Rooms.length > 0) isRoom = checkFilter(Rooms, e.RoomName);
            if (Types.length > 0) isType = checkFilter(Types, e.Type);

            if (Extras.length > 0) {
                const negativeFilters = Extras.filter((f) =>
                    f.startsWith("!"),
                ).map((f) => f.slice(1)); // ["Spa", "Gym"] etc.

                const positiveFilters = Extras.filter(
                    (f) => !f.startsWith("!"),
                );

                const passesNegativeFilter = (e.Extras ?? []).every(
                    (extra) => !negativeFilters.includes(extra.Description),
                );
                const passesPositiveFilter = (e.Extras ?? []).some((extra) =>
                    positiveFilters.includes(extra.Description),
                );

                isExtras = passesNegativeFilter && passesPositiveFilter;
            }

            if (VoucherNames.length > 0) {
                isVoucherName = checkFilter(VoucherNames, e.VoucherName);
            }

            // TODO: Review "Vouchers usually have same CheckIn, CheckOut y ResDate" real impact.
            // if (isVoucher) {
            //     // Vouchers usually have same CheckIn, CheckOut y ResDate
            //     return (
            //         new Date(from_) <= date &&
            //         date < toPlusOneDay &&
            //         //
            //         filteredCancellation &&
            //         isCountry &&
            //         isOffer &&
            //         isRoom &&
            //         isType &&
            //         isExtras &&
            //         isVoucherName
            //     );
            // }

            const skipBookingFilter = e.__skipBookingFilter === true;

            return (
                fromArrivalDate <= arrivalDate &&
                arrivalDate < toArrivalPlusOneDay &&
                (skipBookingFilter ||
                    (fromBookingDate <= bookingDate &&
                        bookingDate < toBookingPlusOneDay)) &&
                fromDate <= date &&
                date < toPlusOneDay &&
                filteredCancellation &&
                isCountry &&
                isOffer &&
                isRoom &&
                isType &&
                isExtras &&
                isVoucherName
            );
        });

        aux = aux.map((e) => {
            let date = new Date(
                helperType(js.dataType, e.ResDate, e.CheckOut, e.CheckIn),
            );
            return {
                ...e,
                [GROUP_DATE_DAY]: date.getUTCDate(),
                [GROUP_DATE_MONTH]: (date.getUTCMonth() + 1)
                    .toString()
                    .padStart(2, "0"),
                [GROUP_DATE_YEAR]: date.getUTCFullYear(),
                [GROUP_DATE_WEEK]: getWeekNumberSince2000(date),
                selectedDate: formatDate(date),
                ...extras,
            };
        });

        // Expand Extras group
        if (js.groupBy?.includes(GROUP_EXTRAS)) {
            let expanded = [];
            aux.forEach((originalBooking) => {
                const bookingExtras = originalBooking.Extras ?? [];
                if (bookingExtras.length > 0) {
                    bookingExtras.forEach((ex) => {
                        let newBooking = {
                            ...originalBooking,
                            [GROUP_EXTRAS]: ex.Description, // TODO: should we change this?
                            // Ajustamos los valores de revenue para que en esta fila
                            // solo cuente el coste de ese extra
                            // TotalPrice: ex.totalCost,
                            revenueSelectedExtra: ex.TotalCost,
                        };

                        // We will expand or not depending on:
                        // - filterExtras bool value
                        // - Extras length
                        // - Extras includes or not that extra
                        // TODO: Review negatives filters and impact in this
                        if (
                            !js?.other?.excludeOtherExtrasIfFiltered ||
                            Extras.length === 0
                        ) {
                            // No hay que filtrar nada, se mete directo
                            expanded.push(newBooking);
                        } else {
                            // Se aplica la misma lógica de filtrado que arriba:
                            // (con posibles filtros negativos "!ExtraX" o positivos "ExtraY")
                            const negativeFilters = Extras.filter((f) =>
                                f.startsWith("!"),
                            ).map((f) => f.slice(1)); // ["Spa", "Gym"], etc.

                            const positiveFilters = Extras.filter(
                                (f) => !f.startsWith("!"),
                            );

                            // ¿Pasa el filtro negativo? -> que NO aparezca en los negativeFilters
                            const passesNegativeFilter =
                                !negativeFilters.includes(ex.Description);

                            // ¿Pasa el filtro positivo?:
                            //    - Si no hay positivos => no hay restricción
                            //    - Si sí hay => el extra debe estar en ese array
                            const passesPositiveFilter =
                                positiveFilters.length === 0 ||
                                positiveFilters.includes(ex.Description);

                            if (passesNegativeFilter && passesPositiveFilter) {
                                newBooking = {
                                    ...newBooking,
                                    Extras: newBooking.Extras.filter((e) =>
                                        Extras.includes(e.Description),
                                    ),
                                };
                                expanded.push(newBooking);
                            }
                        }
                    });
                } else {
                    // expanded.push({ // TODO: should we add this?
                    //     ...originalBooking,
                    //     [GROUP_EXTRAS]: "No extras", // until here
                    //     TotalPrice: 0,
                    //     ExtrasCharge: 0,
                    //     RoomRevenue: 0,
                    // });
                }
            });
            aux = expanded;
        }

        return aux;
    };

    let dataToUseLastYear = FilterData(
        dataToUseComparison,
        fromDayPrevious,
        toDayPrevious,
        fromDayPreviousArrivals,
        toDayPreviousArrivals,
        fromDayPreviousBookings,
        toDayPreviousBookings,
        { period: "lastYear" },
    );

    dataToUse = FilterData(
        dataToUse,
        fromDay,
        toDay,
        fromArrivalDay,
        toArrivalDay,
        fromResDateDay,
        toResDateDay,
        { period: "thisYear" },
    );

    const LENGTH = dataToUse.length;

    // group values by types
    let keys_groups = [...(js.groupBy ?? [])]; // ...timeGroups];

    // Fix case when no keys because it is voucher type
    dataToUse.forEach((obj) => {
        if (obj.Type === "Voucher") {
            keys_groups.forEach((key) => {
                if (obj[key] === null) obj[key] = "Voucher";
            });
        }
    });

    dataToUseLastYear.forEach((obj) => {
        if (obj.Type === "Voucher") {
            keys_groups.forEach((key) => {
                if (obj[key] === null) obj[key] = "Voucher";
            });
        }
    });

    // const areKeysTimeBased = keys_groups.every((key) =>
    //     [GROUP_DATE_MONTH, GROUP_DATE_YEAR, GROUP_DATE_DAY].includes(key)
    // );

    // if (Comparison === DEFAULT_COMPARISON) {
    //     fromDayPrevious.setFullYear(fromDayPrevious.getFullYear() - 1);
    //     toDayPrevious.setFullYear(toDayPrevious.getFullYear() - 1);
    // } else if (Comparison === "previousMonth") {
    //     fromDayPrevious.setMonth(fromDayPrevious.getMonth() - 1);
    //     toDayPrevious.setMonth(toDayPrevious.getMonth() - 1);
    // }

    const hasDateGroup = keys_groups.some((key) =>
        [
            GROUP_DATE_YEAR,
            GROUP_DATE_MONTH,
            GROUP_DATE_DAY,
            GROUP_DATE_WEEK,
        ].includes(key),
    );

    let groups = groupBy(dataToUse, ...keys_groups);
    let groupsLastYear = groupBy(
        dataToUseLastYear.map((e) => {
            const comparisonDateGroup = {
                [GROUP_DATE_DAY]: e[GROUP_DATE_DAY],
                [GROUP_DATE_MONTH]: e[GROUP_DATE_MONTH],
                [GROUP_DATE_YEAR]: e[GROUP_DATE_YEAR],
                [GROUP_DATE_WEEK]: e[GROUP_DATE_WEEK],
                selectedDate: e.selectedDate,
            };

            if (!hasDateGroup) {
                return {
                    ...e,
                    comparisonDateGroup,
                };
            }

            let date = dayjs.utc(e.selectedDate);

            if (!date.isValid()) {
                date = dayjs
                    .utc()
                    .year(Number(e[GROUP_DATE_YEAR]))
                    .month(Number(e[GROUP_DATE_MONTH]) - 1)
                    .date(Number(e[GROUP_DATE_DAY] ?? 1))
                    .startOf("day");
            }

            if (
                comparisonBase === COMPARISON_PREVIOUS_YEAR ||
                comparisonBase === COMPARISON_SAME_TIME_LAST_YEAR
            ) {
                date = date.add(1, "year");
            } else if (comparisonBase === COMPARISON_PREVIOUS_MONTH) {
                date = date.add(1, "month");
            } else if (comparisonBase === COMPARISON_PREVIOUS_WEEK) {
                date = date.add(7, "day");
            } else if (
                comparisonBase === COMPARISON_CUSTOM &&
                js.filters?.ComparisonCustom?.fromDay &&
                js.filters?.ComparisonCustom?.toDay
            ) {
                const currentDays =
                    dayjs.utc(toDay).diff(dayjs.utc(fromDay), "day") + 1;
                const customDays =
                    dayjs
                        .utc(js.filters.ComparisonCustom.toDay)
                        .diff(
                            dayjs.utc(js.filters.ComparisonCustom.fromDay),
                            "day",
                        ) + 1;

                if (currentDays === customDays) {
                    date = date.add(
                        dayjs
                            .utc(fromDay)
                            .diff(
                                dayjs.utc(js.filters.ComparisonCustom.fromDay),
                                "day",
                            ),
                        "day",
                    );
                }
            }

            return {
                ...e,
                comparisonDateGroup,
                [GROUP_DATE_YEAR]: date.year(),
                [GROUP_DATE_MONTH]: String(date.month() + 1).padStart(2, "0"),
                [GROUP_DATE_DAY]: date.date(),
                [GROUP_DATE_WEEK]: getWeekNumberSince2000(date.toDate()),
                selectedDate: formatDate(date),
            };
        }),
        ...keys_groups,
    );

    const groupsSet = new Set(Object.keys({ ...groups, ...groupsLastYear }));

    let entries = Array.from(groupsSet)
        .map((k) => {
            const dataThisYear = groups[k] ?? [];
            const dataLastYear = groupsLastYear[k] ?? [];

            const result =
                dataThisYear.length > 0 ? processFn(dataThisYear, hotelID) : {};
            const resultOneYearBefore =
                dataLastYear.length > 0 ? processFn(dataLastYear, hotelID) : {};

            // Calculate fromDateGroup and toDateGroup for current data
            if (dataThisYear.length > 0) {
                const representative = dataThisYear[0];
                const { fromDateGroup, toDateGroup } = getDateRangeForGroup(
                    representative,
                    keys_groups,
                    js.fromDay,
                    js.toDay,
                );
                result.fromDateGroup = fromDateGroup;
                result.toDateGroup = toDateGroup;

                applySessionsMetrics(
                    result,
                    sessions,
                    fromDateGroup,
                    toDateGroup,
                );
            }

            // Calculate fromDateGroup and toDateGroup for last year data
            if (dataLastYear.length > 0) {
                const representativeLY = dataLastYear[0];

                const {
                    fromDateGroup: fromDateGroupLY,
                    toDateGroup: toDateGroupLY,
                } = getDateRangeForGroup(
                    representativeLY,
                    keys_groups,
                    fromDayPrevious,
                    toDayPrevious,
                );

                const representativeLYForSessions =
                    representativeLY.comparisonDateGroup
                        ? {
                              ...representativeLY,
                              ...representativeLY.comparisonDateGroup,
                          }
                        : representativeLY;

                const {
                    fromDateGroup: fromDateGroupLYForSessions,
                    toDateGroup: toDateGroupLYForSessions,
                } = getDateRangeForGroup(
                    representativeLYForSessions,
                    keys_groups,
                    fromDayPrevious,
                    toDayPrevious,
                );

                resultOneYearBefore.fromDateGroup = fromDateGroupLY;
                resultOneYearBefore.toDateGroup = toDateGroupLY;

                applySessionsMetrics(
                    resultOneYearBefore,
                    sessions,
                    fromDateGroupLYForSessions,
                    toDateGroupLYForSessions,
                );
            }

            // Extras count
            if (js.groupBy?.includes(GROUP_EXTRAS)) {
                result.countSelectedExtra = dataThisYear.length;
                resultOneYearBefore.countSelectedExtra = dataLastYear.length;
            }
            const offerTitleForThisGroup = keys_groups.includes(GROUP_OFFERS)
                ? (groups[k]?.[0]?.[GROUP_OFFERS] ??
                  groupsLastYear[k]?.[0]?.[GROUP_OFFERS])
                : null;

            const offerRenameInfo =
                offerTitleForThisGroup &&
                renameInfoByMain[offerTitleForThisGroup]
                    ? renameInfoByMain[offerTitleForThisGroup]
                    : null;
            return {
                [k]: {
                    result,
                    resultOneYearBefore,
                    keys: keys_groups.map((key) => [
                        key,
                        groups[k]
                            ? groups[k][0][key]
                            : groupsLastYear[k][0][key],
                    ]),
                    data: mergeBookingsWithoutDuplicates(
                        groups[k] || [],
                        groupsLastYear[k] || [],
                    ),
                    offerRenameInfo,
                },
            };
        })
        .reduce((newObj, v) => ({ ...newObj, ...v }), {});

    entries = applyTargetsToEntries({
        entries,
        js,
        dataSimple,
        dataTargets,
        baseComparisonType: comparisonBase,
        processFn: (targetJs) =>
            process_({
                js: targetJs,
                dataBookings,
                hotelID,
                dataSimple,
                dataTargets,
                processFn,
            }),
    });

    return { entries, len: LENGTH };
};

export const processAggregated_ = (args) =>
    process_({
        ...args,
        processFn: processAggregated,
    });

export const formatDate = (date) => {
    if (dayjs.isDayjs(date)) return date.format("YYYY-MM-DD");

    const d = date instanceof Date ? date : new Date(date);
    return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
};

export const getDatesBetween = (startDateStr, endDateStr) => {
    let startDate = new Date(startDateStr);
    let endDate = new Date(endDateStr);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    if (startDate > endDate) {
        [startDate, endDate] = [endDate, startDate];
    }

    const dateArray = [];
    let currentDate = new Date(startDate.getTime());

    while (currentDate <= endDate) {
        dateArray.push(formatDate(currentDate));
        currentDate = new Date(currentDate.setDate(currentDate.getDate() + 1));
        currentDate.setHours(0, 0, 0, 0);
    }

    return dateArray;
};

function newDateUTC(year, month, day) {
    return dayjs.utc().year(year).month(month).date(day).startOf("day");
}
//   ____  ____  _____ ____  _____ _____   ____    _  _____ _____ ____
//  |  _ \|  _ \| ____/ ___|| ____|_   _| |  _ \  / \|_   _| ____/ ___|
//  | |_) | |_) |  _| \___ \|  _|   | |   | | | |/ _ \ | | |  _| \___ \
//  |  __/|  _ <| |___ ___) | |___  | |   | |_| / ___ \| | | |___ ___) |
//  |_|   |_| \_\_____|____/|_____| |_|   |____/_/   \_\_| |_____|____/
export const PresetDates = (type, aux = {}) => {
    let fromDay = dayjs.utc();
    let toDay = dayjs.utc();

    const {
        month = dayjs.utc().month(),
        year = dayjs.utc().year(),
        day = dayjs.utc().date(),
        js = { fromDay, toDay },
    } = aux;

    switch (type) {
        case PRESET_CHOOSE_MONTH:
            fromDay = fromDay.year(year).month(month).date(1);
            toDay = fromDay.endOf("month");
            break;

        case PRESET_SELECTED_PERIOD:
            // Custom date range from `js.fromDay` to `js.toDay`
            fromDay = dayjs.utc(js.fromDay);
            toDay = dayjs.utc(js.toDay);
            break;

        case PRESET_LAST_12_MONTHS:
            // Range covering the last 12 months from today
            fromDay = fromDay.subtract(1, "year").subtract(1, "day");
            toDay = toDay.subtract(1, "day");
            break;

        case PRESET_LAST_6_MONTHS:
            // Range covering the last 6 months from today
            fromDay = fromDay.subtract(6, "month").subtract(1, "day");
            toDay = toDay.subtract(1, "day");
            break;

        case PRESET_LAST_3_MONTHS:
            // Range covering the last 3 months from today
            fromDay = fromDay.subtract(3, "month").subtract(1, "day");
            toDay = toDay.subtract(1, "day");
            break;

        case PRESET_LAST_FULL_MONTH:
            // Range covering the last full month
            fromDay = newDateUTC(year, month - 1, 1);
            toDay = newDateUTC(year, month, 0);
            break;

        case PRESET_LAST_30_DAYS:
            // Range covering the last 30 days from today
            fromDay = fromDay.subtract(30, "day");
            toDay = toDay.subtract(1, "day");
            break;

        case PRESET_LAST_7_DAYS:
            // Range covering the last 7 days from today
            fromDay = fromDay.subtract(7, "day");
            toDay = toDay.subtract(1, "day");
            break;

        case PRESET_LAST_YEAR:
            // Range covering the last year
            fromDay = fromDay
                .year(fromDay.year() - 1)
                .month(0)
                .date(1);
            toDay = toDay.year(toDay.year()).month(0).date(0);
            break;

        case PRESET_YEAR_TO_DATE:
            // Range from the start of the current year to the specified day
            if (
                aux.hasOwnProperty("year") &&
                aux.hasOwnProperty("month") &&
                aux.hasOwnProperty("day")
            ) {
                toDay = newDateUTC(year, month, day - 1);
                fromDay = newDateUTC(toDay.year(), 0, 1);
            } else {
                fromDay = newDateUTC(year, 0, 1);
                toDay = newDateUTC(year, month, day);
                if (!fromDay.isSame(toDay, "day")) {
                    toDay = toDay.subtract(1, "day");
                }
            }
            break;

        case PRESET_MONTH_TO_DATE:
            // Range from the start of the current month to today
            fromDay = fromDay.date(1);
            if (!fromDay.isSame(toDay, "day")) {
                toDay = toDay.subtract(1, "day");
            }
            break;

        case PRESET_THIS_YEAR:
            // Range covering this entire year
            fromDay = fromDay.month(0).date(1);
            toDay = toDay
                .year(toDay.year() + 1)
                .month(0)
                .date(0);
            break;

        case PRESET_THIS_WEEK:
            // Range covering this entire week
            fromDay = fromDay.startOf("week");
            toDay = toDay.endOf("week");
            break;

        case PRESET_THIS_QUARTER:
            // Range covering this entire quarter
            fromDay = newDateUTC(year, Math.floor(month / 3) * 3, 1);
            toDay = newDateUTC(year, Math.floor(month / 3) * 3 + 3, 0);
            break;

        case PRESET_THIS_MONTH:
            // Range covering this entire month
            fromDay = fromDay.date(1);
            toDay = dayjs
                .utc()
                .year(fromDay.year())
                .month(fromDay.month() + 1)
                .date(1)
                .subtract(1, "day");
            break;

        case PRESET_TODAY:
            // Today's date range
            break;

        case PRESET_YESTERDAY:
            // Yesterday's date range
            fromDay = fromDay.subtract(1, "day");
            toDay = toDay.subtract(1, "day");
            break;

        case PRESET_TOMORROW:
            // Tomorrow's date range
            fromDay = fromDay.add(1, "day");
            toDay = toDay.add(1, "day");
            break;

        case PRESET_NEXT_7_DAYS:
            // Range covering the next 7 days starting tomorrow
            fromDay = fromDay.add(1, "day");
            toDay = toDay.add(7, "days");
            break;

        case PRESET_NEXT_MONTH:
            // Range covering the next month
            fromDay = dayjs
                .utc()
                .year(dayjs.utc().year())
                .month(dayjs.utc().month() + 1)
                .date(1);
            toDay = dayjs
                .utc()
                .year(dayjs.utc().year())
                .month(dayjs.utc().month() + 2)
                .date(0);
            break;

        case PRESET_NEXT_30_DAYS:
            // Range covering the next 30 days starting tomorrow
            fromDay = fromDay.add(1, "day");
            toDay = toDay.add(30, "days");
            break;

        case PRESET_NEXT_3_MONTHS:
            // Range covering the next 3 months starting tomorrow
            fromDay = fromDay.add(1, "day");
            toDay = fromDay.add(3, "months");
            break;

        case PRESET_NEXT_6_MONTHS:
            // Range covering the next 6 months starting tomorrow
            fromDay = fromDay.add(1, "day");
            toDay = fromDay.add(6, "months");
            break;

        case PRESET_NEXT_12_MONTHS:
            // Range covering the next 12 months starting tomorrow
            fromDay = fromDay.add(1, "day");
            toDay = fromDay.add(1, "year");
            break;

        case PRESET_ANYTIME:
            // Anytime range from the earliest to the latest possible dates
            fromDay = MIN_DATE;
            toDay = MAX_DATE;
            break;

        default:
            break;
    }

    return { fromDay: formatDate(fromDay), toDay: formatDate(toDay) };
};

export const GROUP_COUNTRIES = "Country";
export const GROUP_OFFERS = "OfferTitle";
export const GROUP_ROOM = "RoomName";
export const GROUP_TYPE = "Type";
export const GROUP_VOUCHERS = "VoucherName";
export const GROUP_EXTRAS = "ExtrasGroup";
export const GROUP_SITE_NAME = "SiteName";
export const GROUP_SITE_ID = "SiteID";
export const GROUP_MOBILE_BOOKING = "MobileBooking";

export const GROUP_DATE_YEAR = "Year";
export const GROUP_DATE_MONTH = "Month";
export const GROUP_DATE_DAY = "Day";
export const GROUP_DATE_WEEK = "WeekNumber";

export const GROUPs = [
    GROUP_COUNTRIES,
    GROUP_OFFERS,
    GROUP_ROOM,
    GROUP_TYPE,
    GROUP_VOUCHERS,
    GROUP_DATE_YEAR,
    GROUP_DATE_MONTH,
    GROUP_DATE_DAY,
    GROUP_DATE_WEEK,
    GROUP_EXTRAS,
    GROUP_MOBILE_BOOKING,
];

export const helperType = (type, resOption, outOption, inOption) =>
    type === DATATYPE_RESDATE
        ? resOption
        : type === DATATYPE_CHECKOUT
          ? outOption
          : inOption;

export const groupBy = (items, ...keys) => {
    return items.reduce((accumulator, item) => {
        const groupKey = keys.map((key) => item[key]).join("/");

        if (!accumulator[groupKey]) {
            accumulator[groupKey] = [];
        }

        accumulator[groupKey].push(item);

        return accumulator;
    }, {});
};

export const flat = (data) =>
    Object.keys(data)
        .map((ky) => [...Object.values(data[ky])])
        .flat()
        .flat();

export const GB_NotDate = (js) => {
    return (js.groupBy ?? []).filter((e) => {
        return ![
            GROUP_DATE_DAY,
            GROUP_DATE_MONTH,
            GROUP_DATE_WEEK,
            GROUP_DATE_YEAR,
        ].includes(e);
    });
};

const SESSIONS_GA_TOOLTIP_EXTRA =
    "Sessions data before 2024 used Universal Analytics, while newer data uses GA4, so comparisons may vary due to different tracking methods.";

export const DISPLAY_ELEMENT_HELPER = {
    commissionNotCancelled: {
        // short: "Commission NC",
        short: "Commission not cancelled",
        tooltip: "Commission, for not cancelled",
        upside: false,
        format: { right: "currency", maxDecimals: 2 },
        headerFormat: { right: "€", brackets: true },
        specific: {
            CommissionScreen: "Total",
        },
    },
    commissionCancelled: {
        short: "Commission cancelled",
        tooltip: "Commission, for cancelled",
        upside: false,
        format: { right: "currency", maxDecimals: 2 },
        headerFormat: { right: "€", brackets: true },
    },
    commissionTotal: {
        short: "Commission",
        tooltip: "Commission",
        upside: false,
        format: { right: "currency", maxDecimals: 2 },
        headerFormat: { right: "€", brackets: true },
    },
    commissionRoomsNotCancelled: {
        // short: "Commission rooms NC",
        short: "Commission rooms NC",
        tooltip: "Commission rooms, for not cancelled",
        upside: false,
        format: { right: "currency", maxDecimals: 2 },
        headerFormat: { right: "€", brackets: true },
        specific: {
            CommissionScreen: "Rooms",
        },
    },
    commissionRoomsCancelled: {
        short: "Commission rooms cancelled",
        tooltip: "Commission rooms, for cancelled",
        upside: false,
        format: { right: "currency", maxDecimals: 2 },
        headerFormat: { right: "€", brackets: true },
    },
    commissionRoomsTotal: {
        short: "Commission rooms",
        tooltip: "Commission rooms",
        upside: false,
        format: { right: "currency", maxDecimals: 2 },
        headerFormat: { right: "€", brackets: true },
    },

    commissionVouchers: {
        // short: "Commission vouchers",
        short: "Vouchers",
        tooltip: "Commission, for vouchers",
        upside: false,
        format: { right: "currency", maxDecimals: 2 },
        headerFormat: { right: "€", brackets: true },
    },
    commissionPercentageFromVouchers: {
        short: "Commission % from vouchers",
        tooltip: "Commission %, for vouchers",
        upside: false,
        format: { maxDecimals: 2 },
        headerFormat: { right: "%", brackets: true },
    },
    commissionPercentageFromRooms: {
        short: "Commission % from rooms",
        tooltip: "Commission %, for rooms",
        upside: false,
        format: { maxDecimals: 2 },
        headerFormat: { right: "%", brackets: true },
    },

    //
    ABWNotCancelled: {
        short: "ABW NC",
        tooltip: "Average Booking Window, for not cancelled",
        upside: false,
        format: { right: " days", maxDecimals: 2 },
    },
    ABWCancelled: {
        short: "ABW cancelled",
        tooltip: "Average Booking Window, for cancelled bookings",
        upside: true,
        format: { right: " days", maxDecimals: 2 },
    },
    ABWTotal: {
        short: "Avg. booking window (ABW)",
        abbreviation: "ABW",
        tooltip:
            "Average Booking Window Total, for cancelled and not cancelled",
        upside: false,
        format: { right: " days", maxDecimals: 2 },
    },
    // For calculation purposes
    BWTotal: {
        short: "Booking window (BW)",
        abbreviation: "BW",
        tooltip: "Booking Window Total, for cancelled and not cancelled",
        upside: false,
        format: { right: " days", maxDecimals: 2 },
    },
    //
    ALoSNotCancelled: {
        short: "ALoS NC",
        tooltip: "Average Length of Stay, for not cancelled bookings",
        upside: false,
        format: { right: " days", maxDecimals: 2 },
    },
    ALoSCancelled: {
        short: "ALoS cancelled",
        tooltip: "Average Length of Stay, for cancelled bookings",
        upside: true,
        format: { right: " days", maxDecimals: 2 },
    },
    ALoSTotal: {
        short: "Avg. length of stay (ALoS)",
        abbreviation: "ALoS",
        tooltip:
            "Average Length of Stay, for cancelled and not cancelled bookings",
        upside: false,
        format: { right: " days", maxDecimals: 2 },
    },
    ADRNotCancelled: {
        short: "ADR NC",
        tooltip: "Average daily rate, for not cancelled bookings",
        upside: false,
        format: { left: "currency" },
    },
    ADRCancelled: {
        short: "ADR cancelled",
        tooltip: "Average Daily Rate, for cancelled bookings",
        upside: true,
        format: { left: "currency" },
    },
    ADRTotal: {
        short: "Avg. daily rate (ADR)",
        abbreviation: "ADR",
        tooltip: "Average Daily Rate, for cancelled and not cancelled bookings",
        upside: false,
        format: { left: "currency" },
    },
    bookingsNotCancelled: {
        short: "Bookings",
        tooltip: "Number of not cancelled bookings",
        upside: false,
    },
    bookingsCancelled: {
        short: "Cancelled bookings",
        tooltip: "Number of cancelled bookings",
        upside: true,
    },
    bookingsTotal: {
        short: "Bookings (total)",
        tooltip: "Number of cancelled and not cancelled bookings",
        specific: {
            ArrivalReport: "Arrivals",
        },
        upside: false,
        important: true,
    },
    bookingsRooms: {
        short: "Bookings rooms",
        specific: {
            // FiltersTableReport: "Bookings rooms",
            ArrivalReport: "Arrivals",
        },
        tooltip: "Number of cancelled and not cancelled bookings rooms only",
        upside: false,
    },
    bookingsMobile: {
        short: "Bookings mobile",
        tooltip: "Number of bookings made from mobile",
        upside: false,
    },
    bookingsNotMobile: {
        short: "Bookings desktop",
        tooltip: "Number of bookings not made from mobile",
        upside: false,
    },
    revenueNotCancelled: {
        short: "Revenue NC",
        tooltip: "Amount of not cancelled revenue",
        upside: false,
        format: { left: "currency" },
    },
    revenueCancelled: {
        short: "Revenue cancelled",
        tooltip: "Amount of cancelled revenue",
        upside: true,
        format: { left: "currency" },
    },
    revenueTotal: {
        short: "Total revenue",
        tooltip: "Amount of cancelled and not cancelled revenue",
        upside: false,
        important: true,
        format: { left: "currency" },
        headerFormat: { right: "currency", brackets: true },
    },
    revenueRoom: {
        short: "Room revenue",
        tooltip: "Amount of cancelled or not cancelled room revenue only",
        upside: false,
        format: { left: "currency" },
        headerFormat: { right: "currency", brackets: true },
    },
    revenueExtras: {
        short: "Extras revenue",
        tooltip: "Amount of cancelled or not cancelled revenue extras",
        upside: false,
        format: { left: "currency" },
        headerFormat: { right: "currency", brackets: true },
    },
    revenueRoomAndExtras: {
        short: "Room revenue + extras",
        tooltip:
            "Amount of cancelled or not cancelled for room revenue + extras",
        upside: false,
        format: { left: "currency" },
        headerFormat: { right: "currency", brackets: true },
        specific: {
            // FiltersTableReport: "Bookings rooms",
            PacingReport: "Total room & extras revenue",
        },
    },
    revenueVouchers: {
        short: "Vouchers revenue",
        tooltip: "Amount of cancelled or not cancelled revenue vouchers",
        upside: false,
        format: { left: "currency" },
        headerFormat: { right: "currency", brackets: true },
        isVoucher: true,
    },
    revenueTotalMobile: {
        short: "Total revenue mobile",
        tooltip: "Total revenue from mobile bookings",
        upside: false,
        format: { left: "currency" },
        headerFormat: { right: "currency", brackets: true },
    },
    revenueTotalNotMobile: {
        short: "Total revenue desktop",
        tooltip: "Total revenue from non-mobile bookings",
        upside: false,
        format: { left: "currency" },
        headerFormat: { right: "currency", brackets: true },
    },
    revenueRoomMobile: {
        short: "Room revenue mobile",
        tooltip: "Room revenue from mobile bookings",
        upside: false,
        format: { left: "currency" },
        headerFormat: { right: "currency", brackets: true },
    },
    revenueRoomNotMobile: {
        short: "Room revenue desktop",
        tooltip: "Room revenue from non-mobile bookings",
        upside: false,
        format: { left: "currency" },
        headerFormat: { right: "currency", brackets: true },
    },
    roomNightsNotCancelled: {
        short: "Room nights NC",
        tooltip: "Number of not cancelled Room Nights",
        upside: false,
    },
    roomNightsCancelled: {
        short: "Room nights cancelled",
        tooltip: "Number of cancelled Room Nights",
        upside: true,
    },
    roomNightsTotal: {
        short: "Room nights",
        tooltip: "Number of cancelled and not cancelled Room Nights",
        upside: false,
        important: true,
    },
    revenueNettNotCancelled: {
        short: "Revenue nett NC",
        tooltip: "Amount of not cancelled revenue (nett)",
        upside: false,
        format: { left: "currency" },
    },
    revenueNettCancelled: {
        short: "Revenue nett cancelled",
        tooltip: "Amount of cancelled revenue (nett)",
        upside: true,
        format: { left: "currency" },
    },
    revenueNettTotal: {
        short: "Revenue nett",
        tooltip: "Amount of cancelled and not cancelled revenue (nett)",
        upside: false,
        format: { left: "currency" },
    },
    cancellationRate: {
        short: "Cancellation rate",
        tooltip: "Cancellation Rate for the given period and group",
        upside: true,
        format: { right: "%", maxDecimals: 2 },
        headerFormat: { right: "%", brackets: true },
    },
    vouchersCount: {
        short: "Vouchers sold",
        tooltip:
            "Number of Vouchers in total bought, many vouchers could being purchased in the same booking",
        upside: false,
        isVoucher: true,
    },
    vouchersBookings: {
        short: "Vouchers purchases",
        tooltip:
            "Times that vouchers had being bought, a bought could have multiple vouchers",
        upside: false,
        isVoucher: true,
    },
    voucherMin: {
        short: "Voucher min",
        tooltip: "Voucher's minimum Total Price",
        upside: false,
        format: { left: "currency" },
        isVoucher: true,
    },
    voucherMax: {
        short: "Voucher max",
        tooltip: "Voucher's maximum Total Price",
        upside: false,
        format: { left: "currency" },
        isVoucher: true,
    },
    bookingsWithExtras: {
        short: "Bookings with Extras",
        tooltip: "Number of bookings that include Extras",
        upside: false,
    },
    numberOfExtras: {
        short: "Total Extras Purchased",
        tooltip: "Number of Extras through all bookings",
        upside: false,
    },
    extrasByType: {
        short: "Extras Revenue and count by type",
        tooltip: "Extras revenue and cunt by type",
        upside: false,
    },
    countSelectedExtra: {
        short: "Total Extra Purchased",
        tooltip: "If there is a selected extra, number of this extras sold",
        upside: false,
        specific: {
            ReportScreen: "Total purchased",
        },
    },
    revenueSelectedExtra: {
        short: "Total Extra Revenue",
        tooltip: "If there is a selected extra, revenue of this extras",
        upside: false,
        specific: {
            ReportScreen: "Extras revenue",
        },
    },
    sessionsTotal: {
        short: "Sessions",
        tooltip: "Website sessions in the selected period",
        upside: false,
        important: true,
        tooltipExtra: SESSIONS_GA_TOOLTIP_EXTRA,
    },
    bookingEngineSessions: {
        short: "BE sessions",
        tooltip: "Booking Engine sessions in the selected period",
        upside: false,
        tooltipExtra: SESSIONS_GA_TOOLTIP_EXTRA,
    },
    bookingEngineSessionsRate: {
        short: "BE sessions rate",
        tooltip: "Booking Engine sessions divided by website sessions",
        upside: false,
        format: { right: "%", maxDecimals: 2 },
        headerFormat: { right: "%", brackets: true },
        tooltipExtra: SESSIONS_GA_TOOLTIP_EXTRA,
    },
    perSessionValue: {
        short: "Per session value",
        tooltip: "Total revenue divided by website sessions",
        upside: false,
        format: { left: "currency", maxDecimals: 2 },
        headerFormat: { right: "currency", brackets: true },
        tooltipExtra: SESSIONS_GA_TOOLTIP_EXTRA,
    },
    perBESessionValue: {
        short: "Per Booking Engine session value",
        tooltip: "Total revenue divided by Booking Engine sessions",
        upside: false,
        format: { left: "currency", maxDecimals: 2 },
        headerFormat: { right: "currency", brackets: true },
        tooltipExtra: SESSIONS_GA_TOOLTIP_EXTRA,
    },
    websiteConversionRate: {
        short: "Website conversion rate",
        tooltip: "Bookings divided by website sessions",
        upside: false,
        format: { right: "%", maxDecimals: 2 },
        headerFormat: { right: "%", brackets: true },
        tooltipExtra: SESSIONS_GA_TOOLTIP_EXTRA,
    },
    BEConversionRate: {
        short: "BE conversion rate",
        tooltip: "Bookings divided by Booking Engine sessions",
        upside: false,
        format: { right: "%", maxDecimals: 2 },
        headerFormat: { right: "%", brackets: true },
        tooltipExtra: SESSIONS_GA_TOOLTIP_EXTRA,
    },
    fromDate: {},
    toDate: {},
    fromDateGroup: {},
    toDateGroup: {},
};

export const getMetricName = (
    metric,
    place = "default",
    metrics = DISPLAY_ELEMENT_HELPER,
) => {
    const metricInfo = metrics[metric];

    if (!metricInfo) {
        return null;
    }
    if (metricInfo.specific && metricInfo.specific[place]) {
        return metricInfo.specific[place];
    }
    return metricInfo.short;
};

export const CANCELLATIONS_FILTER = {
    included: {
        label: "Included",
        includeCancellation: (element) => true,
        hideKey: "CANCELLATIONS_INCLUDED",
    },
    excluded: {
        label: "Excluded",
        includeCancellation: (element) => !element.CancelStatus,
        hideKey: "CANCELLATIONS_EXCLUDED",
    },
    only: {
        label: "Only",
        includeCancellation: (element) => element.CancelStatus,
        hideKey: "CANCELLATIONS_ONLY",
    },
};

export const COMPARISON_PREVIOUS_YEAR = "previousYear";
export const COMPARISON_PREVIOUS_MONTH = "previousMonth";
export const COMPARISON_PREVIOUS_WEEK = "previousWeek";
export const COMPARISON_SAME_TIME_LAST_YEAR = "sameTimeLastYear";
export const COMPARISON_CUSTOM = "customComparison";

export const DEFAULT_COMPARISON = COMPARISON_SAME_TIME_LAST_YEAR;

export const COMPARISON_FILTER = {
    [COMPARISON_PREVIOUS_YEAR]: {
        label: "Previous year",
        description:
            "Compares the selected period with the exact same date range one year before. If the selected period includes future dates, the comparison period will still include the full equivalent range from last year.",
    },
    [COMPARISON_PREVIOUS_MONTH]: {
        label: "Previous month",
        description:
            "Compares the selected period with the same date range shifted one month back.",
    },
    [COMPARISON_PREVIOUS_WEEK]: {
        label: "Previous week",
        description:
            "Compares the selected period with the same number of days shifted 7 days back. It is not the previous calendar week.",
    },
    [COMPARISON_SAME_TIME_LAST_YEAR]: {
        label: "Same time last year",
        description:
            "Compares the selected period with the equivalent period last year, capped to the same progress point when the selected period is still in progress. For example, if today is day 4 of the selected month, it compares against the first 4 days of the same month last year.",
    },
    [COMPARISON_CUSTOM]: {
        label: "Custom date",
        description:
            "Compares the selected period with a custom date range chosen manually. The comparison values remain assigned to their actual calendar dates.",
    },
};
